/**
 * Datum i Hyria.
 *
 * Ett fält som betyder "ett datum" lagras som ÅÅÅÅ-MM-DD — aldrig som
 * ISO-tidsstämpel, aldrig som Firestore Timestamp. Tre vägar bröt mot det:
 * modalerna skickade toISOString(), BankID-flödet skrev serverTimestamp(),
 * och mallmotorn renderade vad som råkade ligga i databasen. Resultatet
 * var "2027-11-29T23:00:00.000Z" i ett mejl till en styrelse.
 *
 * Allt som kommer in normaliseras med toDateOnly. Allt som visas går genom
 * formatDateSv (datum) eller formatMoment (tidsstämplar).
 *
 * Speglas i functions/src/utils/dates.ts — functions är ett eget npm-paket.
 */

const TZ = 'Europe/Stockholm';

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Vad som helst som liknar ett datum → Date, eller null.
 *
 * Ett rent ÅÅÅÅ-MM-DD tolkas som klockan tolv det datumet, inte som
 * UTC-midnatt (vilket `new Date('2026-09-10')` gör). Annars flyttar en
 * svensk tidszon datumet en dag bakåt i formateringen.
 */
function toDate(value: unknown): Date | null {
  if (value === null || value === undefined || value === '') return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'object') {
    const v = value as {
      toDate?: () => Date;
      seconds?: number;
      _seconds?: number;
    };
    // Firestore Timestamp i klienten/admin-SDK.
    if (typeof v.toDate === 'function') return toDate(v.toDate());
    // Samma sak efter att ha passerat ett callable-svar (serialiserad).
    const seconds =
      typeof v.seconds === 'number' ? v.seconds
        : typeof v._seconds === 'number' ? v._seconds
          : null;
    return seconds === null ? null : new Date(seconds * 1000);
  }

  const raw = String(value).trim();
  const m = DATE_ONLY.exec(raw);
  if (m) {
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12, 0, 0);
  }

  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Normalisera till ÅÅÅÅ-MM-DD i svensk tid. Ogiltigt → null.
 *
 * "2027-09-29T22:00:00.000Z" är midnatt 30 september i Stockholm — därför
 * räcker det inte att kapa strängen vid "T".
 */
export function toDateOnly(value: unknown): string | null {
  if (typeof value === 'string' && DATE_ONLY.test(value.trim())) {
    return value.trim();
  }
  const d = toDate(value);
  if (!d) return null;
  // sv-SE ger ÅÅÅÅ-MM-DD utan extra alternativ.
  return d.toLocaleDateString('sv-SE', { timeZone: TZ });
}

/** Datum för människor: "30 november 2027". Tomt eller ogiltigt → "". */
export function formatDateSv(value: unknown): string {
  const dateOnly = toDateOnly(value);
  const d = toDate(dateOnly);
  if (!d) return '';
  return d.toLocaleDateString('sv-SE', {
    timeZone: TZ,
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/**
 * Tidsstämpel för visning som datum: "2026-09-10". Ogiltigt → null.
 *
 * Skillnaden mot toDateOnly är avsikten: det här är för fält som faktiskt
 * är tidpunkter (createdAt, processedDate) och som ska visas som datum,
 * inte lagras om.
 */
export function formatMoment(value: unknown): string | null {
  const d = toDate(value);
  if (!d) return null;
  return d.toLocaleDateString('sv-SE', { timeZone: TZ });
}
